import React, { useEffect, useState } from 'react';
import { getDownloadURL, ref as storageRef, uploadBytesResumable } from 'firebase/storage';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { db, storage } from '../../firebaseconf';
import { collection, query, where, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import Header from '../../components/header';
import Preloader from '../../components/preloader';
import Layout from '../../components/layout';
import { styled } from '@mui/material/styles';
import {
    Alert,
    Box,
    Typography,
    Card,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    AccordionActions,
    Chip,
    Button,
    CircularProgress
} from '@mui/material';

export default function SubmitAssignment() {
    const [uploadedUrl, setUploadedUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const [alertVisible, setAlertVisible] = useState(false);
    const [error, setError] = useState('');
    const [studentData, setStudentData] = useState(null);
    const [assignments, setAssignments] = useState([]);
    const [expanded, setExpanded] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({});
    const [submittedIds, setSubmittedIds] = useState(new Set());
    const fulldate = new Date().toISOString().split('T')[0];

    const VisuallyHiddenInput = styled('input')({
        clip: 'rect(0 0 0 0)',
        clipPath: 'inset(50%)',
        height: 1,
        overflow: 'hidden',
        position: 'absolute',
        bottom: 0,
        left: 0,
        whiteSpace: 'nowrap',
        width: 1,
    });

    useEffect(() => {
        const loggedUser = JSON.parse(sessionStorage.getItem('logged_user'));
        if (!loggedUser || !loggedUser.institution_id) return;

        setStudentData(loggedUser);

        const instId = loggedUser.institution_id;
        
        // Extract branch from profileId for filtering
        const extractBranch = (profileId) => {
            if (!profileId) return '';
            const parts = profileId.split('_');
            return parts.length >= 2 ? parts[1].toUpperCase() : '';
        };
        const studentBranch = extractBranch(loggedUser.profileId);

        // Fetch all active assignments for student's institution
        const assignmentsRef = collection(db, 'institutions', instId, 'assignments');
        let assignmentsQuery = query(assignmentsRef, where('status', '==', 'active'));

        getDocs(assignmentsQuery).then(snap => {
            let allAssignments = snap.docs.map(d => ({ id: d.id, ...d.data() }));
            
            // Filter by student's branch if assignments have branch specified
            allAssignments = allAssignments.filter(a => 
                !a.branch || a.branch.toUpperCase() === studentBranch
            );
            
            // Sort by due date (earliest first)
            allAssignments.sort((a, b) => {
                const dateA = a.dueDate ? new Date(a.dueDate) : new Date(0);
                const dateB = b.dueDate ? new Date(b.dueDate) : new Date(0);
                return dateA - dateB;
            });
            setAssignments(allAssignments);
        }).catch(err => {
            console.error('Error fetching assignments:', err);
            setError('Failed to load assignments');
        });

        // Check which ones this student already submitted
        const submissionsRef = collection(db, 'institutions', instId, 'submissions');
        getDocs(query(submissionsRef, where('student_id', '==', loggedUser.id || loggedUser.uid))).then(snap => {
            const ids = new Set(snap.docs.map(d => d.data().assignment_id).filter(Boolean));
            setSubmittedIds(ids);
        }).catch(err => {
            console.error('Error fetching submissions:', err);
        });
    }, []);

    const handleChange = (panel) => (event, newExpanded) => {
        setExpanded(newExpanded ? panel : false);
        setUploadedUrl('');
        setError('');
    };

    const handleFileUpload = async (file, assignmentId) => {
        if (!file) return;
        setError('');
        setUploadProgress(prev => ({ ...prev, [assignmentId]: 0 }));

        const fileRef = storageRef(storage, `assignments/${assignmentId}/${studentData.id}_${file.name}`);
        const uploadTask = uploadBytesResumable(fileRef, file);

        uploadTask.on('state_changed',
            (snapshot) => {
                const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
                setUploadProgress(prev => ({ ...prev, [assignmentId]: progress }));
            },
            (err) => {
                setError('Upload failed: ' + err.message);
                setUploadProgress(prev => ({ ...prev, [assignmentId]: 0 }));
            },
            async () => {
                const fileUrl = await getDownloadURL(fileRef);
                setUploadedUrl(fileUrl);
                setUploadProgress(prev => ({ ...prev, [assignmentId]: 100 }));
            }
        );
    };

    const handleSubmit = async (assignment) => {
        if (!uploadedUrl) {
            setError('Please upload a file first.');
            return;
        }

        setLoading(true);
        setError('');
        try {
            await addDoc(collection(db, 'institutions', studentData.institution_id, 'submissions'), {
                assignment_id: assignment.id,
                assignment_title: assignment.title,
                student_id: studentData.id,
                student_name: studentData.name,
                class: studentData.class || '',
                subject: assignment.subject || '',
                semester: assignment.semester || '',
                submittedDate: fulldate,
                pdfUrl: uploadedUrl,
                status: 'submitted',
                checked: false,
                marks_obtained: 0,
                total_marks: assignment.total_marks || 0,
                institution_id: studentData.institution_id,
                createdAt: serverTimestamp()
            });

            setSubmittedIds(prev => new Set([...prev, assignment.id]));
            setUploadedUrl('');
            setAlertVisible(true);
            setTimeout(() => setAlertVisible(false), 3000);
            setExpanded(false);
        } catch (error) {
            console.error('Error submitting assignment:', error);
            setError(`Failed to submit: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateValue) => {
        if (!dateValue) return 'N/A';
        try {
            return new Date(dateValue).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
        } catch { return 'N/A'; }
    };

    const isOverdue = (dueDate) => {
        if (!dueDate) return false;
        return new Date(dueDate) < new Date();
    };

    return (
        <Layout>
            <Header title="Submit Assignment" />
            {alertVisible && <Alert severity="success" sx={{ m: 2 }}>Assignment submitted successfully! ✅</Alert>}
            {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}
            {loading && <Preloader />}

            <Box sx={{ p: 3 }}>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
                    Active Assignments ({assignments.length})
                </Typography>

                {assignments.length === 0 ? (
                    <Card sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                        <AssignmentIcon sx={{ fontSize: 60, mb: 2, opacity: 0.3 }} />
                        <Typography>No active assignments found. Check back later.</Typography>
                    </Card>
                ) : (
                    assignments.map(assignment => {
                        const alreadySubmitted = submittedIds.has(assignment.id);
                        const overdue = isOverdue(assignment.dueDate);

                        return (
                            <Accordion
                                key={assignment.id}
                                expanded={expanded === assignment.id}
                                onChange={handleChange(assignment.id)}
                                sx={{ mb: 2, borderRadius: '8px !important', boxShadow: 2 }}
                            >
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', width: '100%' }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, flexGrow: 1 }}>
                                            {assignment.title}
                                        </Typography>
                                        <Chip label={assignment.subject || 'N/A'} size="small" color="primary" variant="outlined" />
                                        <Chip
                                            label={`Due: ${formatDate(assignment.dueDate)}`}
                                            size="small"
                                            color={overdue ? 'error' : 'warning'}
                                            variant="outlined"
                                        />
                                        {alreadySubmitted && (
                                            <Chip label="✓ Submitted" size="small" color="success" />
                                        )}
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Typography sx={{ mb: 1 }}><strong>Description:</strong> {assignment.description || 'No description'}</Typography>
                                    <Typography sx={{ mb: 1 }}><strong>Total Marks:</strong> {assignment.total_marks || 'N/A'}</Typography>
                                    <Typography sx={{ mb: 1 }}><strong>Teacher:</strong> {assignment.teacher_name || 'N/A'}</Typography>

                                    {!alreadySubmitted ? (
                                        <Box sx={{ mt: 2 }}>
                                            <Button
                                                component="label"
                                                variant="contained"
                                                startIcon={<CloudUploadIcon />}
                                                disabled={loading}
                                            >
                                                {uploadProgress[assignment.id] > 0 && uploadProgress[assignment.id] < 100
                                                    ? `Uploading ${uploadProgress[assignment.id]}%`
                                                    : uploadedUrl && expanded === assignment.id
                                                        ? '✓ File Ready'
                                                        : 'Upload File (PDF)'}
                                                <VisuallyHiddenInput
                                                    type="file"
                                                    accept=".pdf,.doc,.docx,.jpg,.png"
                                                    onChange={(e) => handleFileUpload(e.target.files[0], assignment.id)}
                                                />
                                            </Button>
                                        </Box>
                                    ) : (
                                        <Alert severity="success" sx={{ mt: 2 }}>
                                            You have already submitted this assignment.
                                        </Alert>
                                    )}
                                </AccordionDetails>
                                {!alreadySubmitted && (
                                    <AccordionActions>
                                        <Button
                                            color="primary"
                                            variant="contained"
                                            disabled={loading || !uploadedUrl || expanded !== assignment.id}
                                            onClick={() => handleSubmit(assignment)}
                                        >
                                            {loading ? <CircularProgress size={24} /> : 'Submit Assignment'}
                                        </Button>
                                    </AccordionActions>
                                )}
                            </Accordion>
                        );
                    })
                )}
            </Box>
        </Layout>
    );
}
